import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Passport, Plus } from "shared/assets/icons";
import { Button, Card, Loader, Message, TravelVisaTeaser } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingUserDocuments } from "Bands/utils";

const visaCopy = copy.userTravelVisa;

export const TravelVisas: FC = () => {
  const { travelVisas, setShowCreateModal, setShowEditModal, setShowDeleteModal, getItemActions } =
    useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.travelVisa,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.travelVisa,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingTravelVisas } = useFetchingUserDocuments();

  const content = useMemo(() => {
    if (fetchingTravelVisas) {
      return <Loader />;
    }

    if (!travelVisas?.length) {
      return <Message message={visaCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {travelVisas?.map(visa => (
          <TravelVisaTeaser key={visa.id} {...visa} actions={getActions(visa.id)} />
        ))}
      </GridContainer>
    );
  }, [travelVisas, fetchingTravelVisas, getActions]);

  return (
    <Card icon={Passport} title={visaCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.travelVisa)}
          data-testid="add_travel_visa_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{visaCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
